import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileText, Download, Eye, Search, Filter, ShieldCheck,
  Users, Building2, Calendar, RefreshCw, CheckCircle2, Lock,
  ExternalLink, Hash, MapPin, Smartphone, Mail
} from 'lucide-react';
import { API_URL as API } from '../../config/api';
import AgreementModal from '../AgreementModal';

export default function AdminAgreements({ token }) {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | customer | partner
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [counts, setCounts] = useState({ total: 0, customerCount: 0, partnerCount: 0 });

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agreements/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreements(res.data.agreements || []);
      setCounts({
        total: res.data.total || 0,
        customerCount: res.data.customerCount || 0,
        partnerCount: res.data.partnerCount || 0
      });
    } catch (err) {
      console.error('Failed to fetch user agreements:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements();
  }, [token]);

  // Filtering
  const filtered = agreements.filter(item => {
    const matchesType = (
      typeFilter === 'all' ||
      (typeFilter === 'customer' && item.agreement_type === 'customer_agreement') ||
      (typeFilter === 'partner' && item.agreement_type === 'partner_agreement')
    );

    const term = search.toLowerCase().trim();
    if (!term) return matchesType;

    const name = (item.user_name || '').toLowerCase();
    const phone = (item.user_phone || '').toLowerCase();
    const email = (item.user_email || '').toLowerCase();
    const sigId = (item.signature_id || '').toLowerCase();
    const hash = (item.signature_hash || '').toLowerCase();

    const matchesSearch = (
      name.includes(term) ||
      phone.includes(term) ||
      email.includes(term) ||
      sigId.includes(term) ||
      hash.includes(term)
    );

    return matchesType && matchesSearch;
  });

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top Header Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        border: '1.5px solid #EEF2E6',
        padding: '1.75rem 2rem',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              background: '#084734',
              color: '#CEF17B',
              borderRadius: '10px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ShieldCheck size={24} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '900', color: '#161616', margin: 0 }}>
                User & Partner Legal Agreements
              </h2>
              <p style={{ margin: '2px 0 0 0', color: '#4b5563', fontSize: '0.85rem' }}>
                Digitally signed contracts, IT Act (2000) electronic consent records & certificate archive.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={fetchAgreements}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#F8FAF7',
            border: '1.5px solid #EEF2E6',
            color: '#084734',
            padding: '10px 18px',
            borderRadius: '10px',
            fontWeight: '700',
            fontSize: '0.88rem',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh Records
        </button>
      </div>

      {/* Summary KPI Badges */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {/* Total Signed */}
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1.5px solid #EEF2E6',
          padding: '1.25rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '14px'
        }}>
          <div style={{ background: '#F0FDF4', padding: '12px', borderRadius: '10px', color: '#166534' }}>
            <FileText size={24} />
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>
              Total Signed Contracts
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#111827' }}>
              {counts.total}
            </div>
          </div>
        </div>

        {/* Customer Agreements */}
        <div
          onClick={() => setTypeFilter('customer')}
          style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: typeFilter === 'customer' ? '2px solid #084734' : '1.5px solid #EEF2E6',
            padding: '1.25rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            cursor: 'pointer'
          }}
        >
          <div style={{ background: '#EFF6FF', padding: '12px', borderRadius: '10px', color: '#1D4ED8' }}>
            <Users size={24} />
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>
              Customer Agreements
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#111827' }}>
              {counts.customerCount}
            </div>
          </div>
        </div>

        {/* Partner Agreements */}
        <div
          onClick={() => setTypeFilter('partner')}
          style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: typeFilter === 'partner' ? '2px solid #084734' : '1.5px solid #EEF2E6',
            padding: '1.25rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            cursor: 'pointer'
          }}
        >
          <div style={{ background: '#FEF3C7', padding: '12px', borderRadius: '10px', color: '#B45309' }}>
            <Building2 size={24} />
          </div>
          <div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>
              Venue Partner Agreements
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#111827' }}>
              {counts.partnerCount}
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        background: '#ffffff',
        borderRadius: '14px',
        border: '1.5px solid #EEF2E6',
        padding: '1rem 1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        {/* Type Filter Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: `All Agreements (${counts.total})` },
            { id: 'customer', label: `Customers (${counts.customerCount})` },
            { id: 'partner', label: `Venue Partners (${counts.partnerCount})` },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setTypeFilter(f.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: typeFilter === f.id ? '1.5px solid #084734' : '1.5px solid #EEF2E6',
                background: typeFilter === f.id ? '#084734' : '#F8FAF7',
                color: typeFilter === f.id ? '#CEF17B' : '#374151',
                fontWeight: '700',
                fontSize: '0.82rem',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div style={{ position: 'relative', width: '320px' }}>
          <Search size={16} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search by name, phone, certificate ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px 9px 36px',
              borderRadius: '8px',
              border: '1.5px solid #D1D5DB',
              fontSize: '0.85rem',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Main Table of Agreements */}
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        border: '1.5px solid #EEF2E6',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
      }}>
        {loading ? (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6B7280', fontWeight: '700' }}>
            <div style={{
              width: '36px',
              height: '36px',
              border: '3px solid #EEF2E6',
              borderTop: '3px solid #084734',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            Loading digitally signed agreements...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6B7280' }}>
            <FileText size={42} color="#D1D5DB" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontWeight: '800', fontSize: '1.1rem', color: '#111827' }}>No Signed Agreements Found</div>
            <div style={{ fontSize: '0.85rem', marginTop: '4px' }}>
              {search ? 'Try adjusting your search criteria.' : 'Signed user agreements will appear here as players and partners register.'}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#F8FAF7', borderBottom: '1.5px solid #EEF2E6', color: '#4B5563', fontWeight: '800', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '14px 20px' }}>Signatory User</th>
                  <th style={{ padding: '14px 20px' }}>Agreement Type</th>
                  <th style={{ padding: '14px 20px' }}>Certificate & Hash</th>
                  <th style={{ padding: '14px 20px' }}>Signed Timestamp</th>
                  <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, index) => {
                  const isPartner = item.agreement_type === 'partner_agreement';
                  const signedDate = item.signed_at ? new Date(item.signed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }) : '—';
                  
                  return (
                    <tr
                      key={item.id || index}
                      style={{
                        borderBottom: '1px solid #F1F5F9',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAF7'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                    >
                      {/* User Info */}
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: isPartner ? '#FEF3C7' : '#EFF6FF',
                            color: isPartner ? '#B45309' : '#1D4ED8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '800',
                            fontSize: '0.88rem'
                          }}>
                            {isPartner ? <Building2 size={18} /> : <Users size={18} />}
                          </div>
                          <div>
                            <div style={{ fontWeight: '800', color: '#111827', fontSize: '0.9rem' }}>
                              {item.user_name || 'Anonymous User'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: '#6B7280', marginTop: '2px' }}>
                              {item.user_phone && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                  <Smartphone size={12} /> {item.user_phone}
                                </span>
                              )}
                              {item.user_email && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                  · <Mail size={12} /> {item.user_email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Agreement Type */}
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          display: 'inline-block',
                          background: isPartner ? '#FEF3C7' : '#EFF6FF',
                          color: isPartner ? '#92400E' : '#1E40AF',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontWeight: '800',
                          fontSize: '0.78rem',
                          textTransform: 'uppercase'
                        }}>
                          {isPartner ? 'Venue Partner Contract' : 'Customer Agreement'}
                        </span>
                        <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '4px', fontWeight: '600' }}>
                          Version {item.version || '1.0'}
                        </div>
                      </td>

                      {/* Certificate & Hash */}
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '800', color: '#084734', fontSize: '0.82rem' }}>
                          <ShieldCheck size={15} color="#16A34A" /> {item.signature_id || 'TRX-LEGAL-RECORD'}
                        </div>
                        {item.ip_address && (
                          <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: '3px' }}>
                            IP: {item.ip_address}
                          </div>
                        )}
                      </td>

                      {/* Signed Timestamp */}
                      <td style={{ padding: '16px 20px', color: '#334155', fontWeight: '600', fontSize: '0.82rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={14} color="#64748B" /> {signedDate}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#16A34A', fontWeight: '800', marginTop: '3px' }}>
                          ✓ IT Act Compliant
                        </div>
                      </td>

                      {/* Action Buttons */}
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          {/* View Agreement in Modal */}
                          <button
                            type="button"
                            onClick={() => setSelectedAgreement(item)}
                            title="View Executed Agreement"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: '#ffffff',
                              border: '1.5px solid #084734',
                              color: '#084734',
                              padding: '7px 14px',
                              borderRadius: '8px',
                              fontWeight: '800',
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#084734'; e.currentTarget.style.color = '#CEF17B'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#084734'; }}
                          >
                            <Eye size={14} /> View
                          </button>

                          {/* Download PDF */}
                          <a
                            href={item.download_url || `${API}/agreements/download/${item.id}`}
                            target="_blank"
                            rel="noreferrer"
                            title="Download Signed PDF Document"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: '#084734',
                              color: '#ffffff',
                              border: '1.5px solid #084734',
                              padding: '7px 14px',
                              borderRadius: '8px',
                              fontWeight: '800',
                              fontSize: '0.78rem',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#063527'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#084734'; }}
                          >
                            <Download size={14} /> Download PDF
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal to View Full Signed Agreement in Plain Paper Mode */}
      {selectedAgreement && (
        <AgreementModal
          isOpen={!!selectedAgreement}
          onClose={() => setSelectedAgreement(null)}
          agreementType={selectedAgreement.agreement_type === 'partner_agreement' ? 'partner' : 'customer'}
          userAgreement={selectedAgreement}
          mandatoryAccept={false}
        />
      )}
    </div>
  );
}
